import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Navigation, X } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MESI = [
  { value: "1", label: "Gennaio" },
  { value: "2", label: "Febbraio" },
  { value: "3", label: "Marzo" },
  { value: "4", label: "Aprile" },
  { value: "5", label: "Maggio" },
  { value: "6", label: "Giugno" },
  { value: "7", label: "Luglio" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Settembre" },
  { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Dicembre" }
];

const SearchFilters = ({ onSearch, initialFilters = {} }) => {
  const [regioni, setRegioni] = useState([]);
  const [province, setProvince] = useState([]);
  const [filters, setFilters] = useState({
    regione: initialFilters.regione || "",
    provincia: initialFilters.provincia || "",
    mese: initialFilters.mese || "",
    useGeolocation: false,
    lat: null,
    lng: null,
    raggio_km: "100"
  });
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    fetchRegioni();
  }, []);

  useEffect(() => {
    if (filters.regione) {
      fetchProvince(filters.regione);
    } else {
      setProvince([]);
    }
  }, [filters.regione]);

  const fetchRegioni = async () => {
    try {
      const res = await axios.get(`${API}/regioni`);
      setRegioni(res.data.regioni);
    } catch (error) {
      console.error("Error fetching regioni:", error);
    }
  };

  const fetchProvince = async (regione) => {
    try {
      const res = await axios.get(`${API}/province/${encodeURIComponent(regione)}`);
      setProvince(res.data.province);
    } catch (error) {
      console.error("Error fetching province:", error);
    }
  };

  const handleGeolocation = () => {
    if (!navigator.geolocation) return;

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newFilters = {
          ...filters,
          useGeolocation: true,
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          regione: "",
          provincia: ""
        };
        setFilters(newFilters);
        setGeoLoading(false);
        onSearch(newFilters);
      },
      () => setGeoLoading(false)
    );
  };

  const clearGeolocation = () => {
    const newFilters = {
      ...filters,
      useGeolocation: false,
      lat: null,
      lng: null
    };
    setFilters(newFilters);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (key === "regione") newFilters.provincia = "";
    setFilters(newFilters);
  };

  const handleSearch = () => {
    onSearch(filters);
  };

  const clearAll = () => {
    const cleared = {
      regione: "",
      provincia: "",
      mese: "",
      useGeolocation: false,
      lat: null,
      lng: null,
      raggio_km: "100"
    };
    setFilters(cleared);
    onSearch(cleared);
  };

  const hasFilters = filters.regione || filters.provincia || filters.mese || filters.useGeolocation;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="search-filters">
      {/* Geolocation */}
      {filters.useGeolocation ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={clearGeolocation}
          className="rounded-lg h-9 gap-1.5"
          data-testid="clear-geolocation-btn"
        >
          <Navigation className="w-3.5 h-3.5" />
          Vicino a me
          <X className="w-3.5 h-3.5" />
        </Button>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={handleGeolocation}
          disabled={geoLoading}
          className="rounded-lg h-9 gap-1.5"
          data-testid="geolocation-btn"
        >
          <Navigation className="w-3.5 h-3.5" />
          {geoLoading ? "..." : "Vicino a me"}
        </Button>
      )}

      {/* Regione */}
      <Select
        value={filters.regione}
        onValueChange={(value) => handleFilterChange("regione", value)}
        disabled={filters.useGeolocation}
      >
        <SelectTrigger className="h-9 w-[140px] rounded-lg text-sm" data-testid="regione-select">
          <SelectValue placeholder="Regione" />
        </SelectTrigger>
        <SelectContent>
          {regioni.map((reg) => (
            <SelectItem key={reg} value={reg}>{reg}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Provincia */}
      <Select
        value={filters.provincia}
        onValueChange={(value) => handleFilterChange("provincia", value)}
        disabled={!filters.regione || filters.useGeolocation}
      >
        <SelectTrigger className="h-9 w-[140px] rounded-lg text-sm" data-testid="provincia-select">
          <SelectValue placeholder="Provincia" />
        </SelectTrigger>
        <SelectContent>
          {province.map((prov) => (
            <SelectItem key={prov} value={prov}>{prov}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Mese */}
      <Select
        value={filters.mese}
        onValueChange={(value) => handleFilterChange("mese", value)}
      >
        <SelectTrigger className="h-9 w-[120px] rounded-lg text-sm" data-testid="mese-select">
          <SelectValue placeholder="Mese" />
        </SelectTrigger>
        <SelectContent>
          {MESI.map((m) => (
            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Search */}
      <Button 
        onClick={handleSearch}
        size="sm"
        className="rounded-lg h-9"
        data-testid="search-btn"
      >
        Cerca
      </Button>

      {/* Clear */}
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearAll}
          className="rounded-lg h-9 text-muted-foreground"
          data-testid="clear-filters-btn"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Cancella
        </Button>
      )}
    </div>
  );
};

export default SearchFilters;
